import {
  computed,
  inject,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { of } from 'rxjs';
import {
  Cambia_Stato_Installatore_Args,
  CambiaStatoInstallatoreGQL,
  DeletePnrrDocumentiGQL,
  DeleteSottoscrizioneDocumentiGQL,
  Energy_Village__Azioni_Prosumer_Sottoscrizione_Enum,
  GetSottoscrizioneInstallatoreDocument,
  GetSottoscrizioneInstallatoreSubscription,
  SearchSottoscrizioniCountDocument,
  SearchSottoscrizioniCountSubscription,
  SearchSottoscrizioniDocument,
  SearchSottoscrizioniSubscription,
  InputMaybe,
  InsertOrUpdateSottoscrizioneGQL,
  Sottoscrizione_Insert_Input,
  Sottoscrizione_On_Conflict,
  Sottoscrizione_Order_By,
} from '../../../../_core/_graphql/generated';
import { ApolloRoleService } from '../../../../_core/_services/apollo-role.service';
import { LoghiStore } from '../../../../_core/_signal-store/_loghi/loghi.store';
import { initialInstallatoreState } from './installatore.state';

export const InstallatoreStore = signalStore(
  { providedIn: 'root' },
  withState(initialInstallatoreState),
  withComputed((store) => ({
    badges: computed(() => {
      const result = store
        .sottoscrizioni_data?.()
        ?.reduce((acc: any, sottoscrizione) => {
          const state = sottoscrizione.stato;
          acc[state] = (acc[state] || 0) + 1;
          return acc;
        }, {});
      return result
        ? Object.entries(result).map(
          ([key, value]) =>
            ({ key, value }) as { key: string; value: number },
        )
        : [];
    }),
    sottoscrizioni_active_filter_count: computed(() => {
      const filter = store.sottoscrizioni_active_filter();
      const search = store.sottoscrizioni_search();
      let count = 0;
      if (filter?.configurazioni?.length) count += filter.configurazioni.length;
      if (filter?.cabina?.length) count += filter.cabina.length;
      if (filter?.stato?.length) count += filter.stato.length;
      if (filter?.richiesta?.length) count += filter.richiesta.length;
      if (search?.trim()) count += 1;
      return count;
    }),
  })),
  withMethods((store, injector = inject(Injector)) => {
    // Registry in-memory (non nel state serializzato)
    const subs = new Map<string, { unsubscribe: () => void }>();

    const setSub = (key: string, sub: { unsubscribe: () => void }) => {
      // se esiste, chiudi quella precedente
      subs.get(key)?.unsubscribe?.();
      subs.set(key, sub);
    };

    const clearSub = (key: string) => {
      subs.get(key)?.unsubscribe?.();
      subs.delete(key);
    };

    const clearAllSubs = (prefix?: string) => {
      for (const [k, s] of subs.entries()) {
        if (!prefix || k.startsWith(prefix)) {
          s?.unsubscribe?.();
          subs.delete(k);
        }
      }
    };

    const uuidRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const asUuid = (v: any): string | null => {
      const candidate =
        typeof v === 'string'
          ? v
          : typeof v?.id === 'string'
            ? v.id
            : typeof v?.value === 'string'
              ? v.value
              : typeof v?.key === 'string'
                ? v.key
                : null;
      if (!candidate) return null;
      const x = candidate.trim();
      return uuidRe.test(x) ? x : null;
    };
    const asInt = (v: any): number | null => {
      const candidate =
        typeof v === 'number'
          ? v
          : typeof v === 'string'
            ? Number(v)
            : typeof v?.id === 'number'
              ? v.id
              : typeof v?.id === 'string'
                ? Number(v.id)
                : typeof v?.value === 'number'
                  ? v.value
                  : typeof v?.value === 'string'
                    ? Number(v.value)
                    : null;
      return Number.isInteger(candidate) ? candidate : null;
    };
    const asText = (v: any): string | null => {
      const candidate =
        typeof v === 'string'
          ? v
          : typeof v?.value === 'string'
            ? v.value
            : typeof v?.key === 'string'
              ? v.key
              : typeof v?.id === 'string'
                ? v.id
                : null;
      if (!candidate) return null;
      const x = candidate.trim();
      return x.length > 0 ? x : null;
    };
    const buildSottoscrizioniArgs = (filter?: any, search?: string) => {
      const p_cabina = (filter?.cabina ?? [])
        .map(asInt)
        .filter((v: number | null): v is number => v != null);
      const p_configurazioni = (filter?.configurazioni ?? [])
        .map(asUuid)
        .filter((v: string | null): v is string => v != null);
      const p_stato = (filter?.stato ?? [])
        .map(asText)
        .filter((v: string | null): v is string => v != null);
      const p_richiesta = (filter?.richiesta ?? [])
        .map(asText)
        .filter((v: string | null): v is string => v != null);

      return {
        p_search: search?.trim() ? search.trim() : null,
        p_stato: p_stato.length > 0 ? p_stato : null,
        p_richiesta: p_richiesta.length > 0 ? p_richiesta : null,
        p_cabina: p_cabina.length > 0 ? p_cabina : null,
        p_configurazioni:
          p_configurazioni.length > 0 ? p_configurazioni : null,
      };
    };
    const toPgArrayText = (values: string[]): string | null => {
      if (!values || values.length === 0) return null;
      const escaped = values.map(
        (v) => `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`,
      );
      return `{${escaped.join(',')}}`;
    };
    const toPgArrayNumber = (values: number[]): string | null => {
      if (!values || values.length === 0) return null;
      return `{${values.join(',')}}`;
    };
    const sanitizeSottoscrizioniArgs = (args: any) => {
      const cfg = Array.isArray(args?.p_configurazioni)
        ? args.p_configurazioni
          .filter((v: any) => typeof v === 'string')
          .map((v: string) => v.trim())
          .filter((v: string) => uuidRe.test(v))
        : [];
      const cab = Array.isArray(args?.p_cabina)
        ? args.p_cabina
          .map((v: any) => Number(v))
          .filter((v: number) => Number.isInteger(v))
        : [];
      const stato = Array.isArray(args?.p_stato)
        ? args.p_stato
          .filter((v: any) => typeof v === 'string' && v.trim().length > 0)
          .map((v: string) => v.trim())
        : [];
      const richiesta = Array.isArray(args?.p_richiesta)
        ? args.p_richiesta
          .filter((v: any) => typeof v === 'string' && v.trim().length > 0)
          .map((v: string) => v.trim())
        : [];
      const p_search =
        typeof args?.p_search === 'string' && args.p_search.trim().length > 0
          ? args.p_search.trim()
          : null;
      return {
        p_search,
        p_stato: toPgArrayText(stato),
        p_richiesta: toPgArrayText(richiesta),
        p_cabina: toPgArrayNumber(cab),
        p_configurazioni: toPgArrayText(cfg),
      };
    };

    return {
      setSottoscrizioneFilterData: (data: any) => {
        patchState(store, {
          sottoscrizioni_filter_data: data,
        });
      },
      clearCerData: () => {
        patchState(store, {
          cabina_data: undefined,
        });
      },
      initSottoscrizioni: (filter?: any, search?: string) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('installatore');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const resolvedSearch = search !== undefined ? search : store.sottoscrizioni_search();

          patchState(store, {
            cabina_data:
              filter?.cabina?.length > 0 ? filter.cabina : undefined,
            sottoscrizioni_filter: !!(
              filter?.stato?.length ||
              filter?.richiesta?.length ||
              filter?.configurazioni?.length > 0 ||
              filter?.cabina?.length > 0 ||
              resolvedSearch?.trim()
            ),
            sottoscrizioni_active_filter: filter,
            sottoscrizioni_search: resolvedSearch,
            sottoscrizioni_page: 0,
            sottoscrizioni_loading: true
          });

          const pageSize = store.sottoscrizioni_page_size();
          const args = sanitizeSottoscrizioniArgs(buildSottoscrizioniArgs(filter, resolvedSearch));

          clearSub('sottoscrizioni');
          const sub = _getter
            .subscribe<SearchSottoscrizioniSubscription>({
              query: SearchSottoscrizioniDocument,
              variables: {
                args,
                limit: pageSize,
                offset: 0,
                order_by: store.sottoscrizioni_order_by(),
              } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.search_sottoscrizioni?.map((s) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: res.data?.search_sottoscrizioni,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('sottoscrizioni', sub);

          clearSub('sottoscrizioniCount');
          const countSub = _getter
            .subscribe<SearchSottoscrizioniCountSubscription>({
              query: SearchSottoscrizioniCountDocument,
              variables: { args },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  sottoscrizioni_total: res.data?.search_sottoscrizioni_aggregate?.aggregate?.count ?? 0,
                });
              },
              error: () => { },
            });
          setSub('sottoscrizioniCount', countSub);

          return sub;
        }),

      changePage: (page: number, requestedPageSize?: number) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('installatore');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const filter = store.sottoscrizioni_active_filter();
          const pageSize = requestedPageSize ?? store.sottoscrizioni_page_size();
          const offset = page * pageSize;
          const args = sanitizeSottoscrizioniArgs(buildSottoscrizioniArgs(filter, store.sottoscrizioni_search()));

          patchState(store, {
            sottoscrizioni_page: page,
            sottoscrizioni_page_size: pageSize,
          });

          clearSub('sottoscrizioni');
          const sub = _getter
            .subscribe<SearchSottoscrizioniSubscription>({
              query: SearchSottoscrizioniDocument,
              variables: {
                args,
                limit: pageSize,
                offset,
                order_by: store.sottoscrizioni_order_by(),
              } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.search_sottoscrizioni?.map((s) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: res.data?.search_sottoscrizioni,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('sottoscrizioni', sub);
          return sub;
        }),
      loadSelectedSottoscrizione: (id: number) => {
        return runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('installatore');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          patchState(store, { selected_sottoscrizione_loading: true });
          clearSub('selectedSottoscrizione');
          const sub = _getter
            .subscribe<GetSottoscrizioneInstallatoreSubscription>({
              query: GetSottoscrizioneInstallatoreDocument,
              variables: {
                id,
              },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  selected_sottoscrizione_loading: false,
                  selected_sottoscrizione: res.data?.sottoscrizione_by_pk,
                });
              },
              error: (error) => {
                patchState(store, {
                  selected_sottoscrizione_loading: false,
                  selected_sottoscrizione: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('selectedSottoscrizione', sub);
          return sub;
        });
      },

      clearSelectedSottoscrizione: () => {
        clearSub('selectedSottoscrizione');
        patchState(store, {
          selected_sottoscrizione_loading: false,
          selected_sottoscrizione: undefined,
        });
      },

      deleteDocumentiSottoscrizione: (id: string[]) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(DeleteSottoscrizioneDocumentiGQL);
          return _mutation
            .mutate(
              {
                where: {
                  id: {
                    _in: id,
                  },
                },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'installatore' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                },
              }),
            );
        });
      },
      deleteDocumentiPnrrSottoscrizione: (id: string[]) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(DeletePnrrDocumentiGQL);
          return _mutation
            .mutate(
              {
                where: {
                  id: {
                    _in: id,
                  },
                },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'installatore' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                },
              }),
            );
        });
      },
      cambiaStato: (args: InputMaybe<Cambia_Stato_Installatore_Args>) => {
        return runInInjectionContext(injector, () => {
          if (
            args?.action !=
            Energy_Village__Azioni_Prosumer_Sottoscrizione_Enum.SalvaPerDopo
          ) {
            const _mutation = inject(CambiaStatoInstallatoreGQL);
            const _toast = inject(MessageService);
            const _translate = inject(TranslateService);
            patchState(store, { selected_sottoscrizione_loading: true });

            return _mutation
              .mutate(
                {
                  args,
                },
                {
                  context: {
                    headers: { 'x-hasura-role': 'installatore' },
                  },
                },
              )
              .pipe(
                tapResponse({
                  next: (res) => {
                    return res;
                  },
                  error: (error) => {
                    patchState(store, {
                      failure: error,
                      selected_sottoscrizione_loading: false,
                    });
                    _toast.add({
                      key: 'global-toast',
                      severity: 'error',
                      summary: _translate.instant('toast.error.title'),
                      detail: _translate.instant('toast.error.detail', {
                        error,
                      }),
                    });
                  },
                }),
              );
          } else {
            return of({});
          }
        });
      },
      insertOrUpdateSottoscrizione: (
        objects: Sottoscrizione_Insert_Input,
        on_conflict: Sottoscrizione_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(InsertOrUpdateSottoscrizioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              {
                objects,
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'installatore' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => res,
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', {
                      error,
                    }),
                  });
                },
              }),
            );
        });
      },
      changeSort: (order_by: Sottoscrizione_Order_By[]) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('installatore');
          const _loghi = inject(LoghiStore);

          const filter = store.sottoscrizioni_active_filter();
          const pageSize = store.sottoscrizioni_page_size();
          const args = sanitizeSottoscrizioniArgs(buildSottoscrizioniArgs(filter, store.sottoscrizioni_search()));

          patchState(store, {
            sottoscrizioni_order_by: order_by,
            sottoscrizioni_page: 0,
          });

          clearSub('sottoscrizioni');
          const sub = _getter
            .subscribe<SearchSottoscrizioniSubscription>({
              query: SearchSottoscrizioniDocument,
              variables: { args, limit: pageSize, offset: 0, order_by } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.search_sottoscrizioni?.map((s) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: res.data?.search_sottoscrizioni,
                });
              },
              error: () => {
                patchState(store, { sottoscrizioni_loading: false });
              },
            });
          setSub('sottoscrizioni', sub);
        }),

      clear: () => {
        clearAllSubs();
        patchState(store, initialInstallatoreState);
      },
      //   insertOrUpdateStagingLotto: (
      //     objects: Staging_Installatore_Lotto_Insert_Input,
      //     on_conflict: Staging_Installatore_Lotto_On_Conflict,
      //   ) => {
      //     return runInInjectionContext(injector, () => {
      //       const _insertOrUpdate = inject(
      //         InsertOrUpdateStagingInstallatoreLottoGQL,
      //       );
      //       const _toast = inject(MessageService);
      //       const _translate = inject(TranslateService);
      //       const _userStore = inject(UserStore);

      //       return _insertOrUpdate
      //         .mutate(
      //           {
      //             objects: { ...objects, auth_id: _userStore.user_data?.()?.sub },
      //             on_conflict,
      //           },
      //           {
      //             context: {
      //               headers: { 'x-hasura-role': 'installatore' },
      //             },
      //           },
      //         )
      //         .pipe(
      //           tapResponse({
      //             next: (res) => res,
      //             error: (error) => {
      //               patchState(store, {
      //                 failure: error,
      //               });
      //               _toast.add({
      //                 key: 'global-toast',
      //                 severity: 'error',
      //                 summary: _translate.instant('toast.error.title'),
      //                 detail: _translate.instant('toast.error.detail', {
      //                   error,
      //                 }),
      //               });
      //             },
      //           }),
      //         );
      //     });
      //   },
      //   insertOrUpdateStagingSottoscrizione: (
      //     objects: Staging_Installatore_Sottoscrizione_Insert_Input,
      //     on_conflict: Staging_Installatore_Sottoscrizione_On_Conflict,
      //   ) => {
      //     return runInInjectionContext(injector, () => {
      //       const _insertOrUpdate = inject(
      //         InsertOrUpdateStagingInstallatoreSottoscrizioneGQL,
      //       );
      //       const _toast = inject(MessageService);
      //       const _translate = inject(TranslateService);
      //       const _userStore = inject(UserStore);
      //       const _loading = inject(LoadingService);
      //       _loading.start();
      //       return _insertOrUpdate
      //         .mutate(
      //           {
      //             objects: { ...objects, auth_id: _userStore.user_data?.()?.sub },
      //             on_conflict,
      //           },
      //           {
      //             context: {
      //               headers: { 'x-hasura-role': 'installatore' },
      //             },
      //           },
      //         )
      //         .pipe(
      //           tapResponse({
      //             next: (res) => {
      //               _loading.stop();
      //               return res;
      //             },
      //             error: (error) => {
      //               patchState(store, {
      //                 failure: error,
      //               });
      //               _loading.stop();
      //               _toast.add({
      //                 key: 'global-toast',
      //                 severity: 'error',
      //                 summary: _translate.instant('toast.error.title'),
      //                 detail: _translate.instant('toast.error.detail', {
      //                   error,
      //                 }),
      //               });
      //             },
      //           }),
      //         );
      //     });
      //   },
      //   initStagingSottoscrizioni: () => {
      //     const _getter = inject(GetStagingInstallatoreSottoscrizioniGQL);
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);

      //     return runInInjectionContext(injector, () => {
      //       return _getter.fetch(
      //         { where: {} },
      //         {
      //           context: {
      //             headers: { 'x-hasura-role': 'installatore' },
      //           },
      //         },
      //       );
      //     }).subscribe({
      //       next: (res) => {
      //         patchState(store, {
      //           staging_sottoscrizione_loading: false,
      //           staging_sottoscrizione_data: res.data?.staging_sottoscrizione,
      //         });
      //       },
      //       error: (error) => {
      //         patchState(store, {
      //           staging_sottoscrizione_loading: false,
      //           staging_sottoscrizione_data: undefined,
      //           failure: error,
      //         });
      //         _toast.add({
      //           key: 'global-toast',
      //           severity: 'error',
      //           summary: _translate.instant(`toast.error.title`),
      //           detail: _translate.instant(`toast.error.detail`, {
      //             error,
      //           }),
      //         });
      //       },
      //     });
      //   },
      //   loadSelectedStagingSottoscrizione: (id: string) => {
      //     const _getter = inject(GetStagingInstallatoreSottoscrizioneGQL);
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);

      //     return runInInjectionContext(injector, () => {
      //       return _getter.fetch({
      //         id: {
      //           _eq: id,
      //         },
      //       });
      //     }).subscribe({
      //       next: (res) => {
      //         patchState(store, {
      //           staging_sottoscrizione_loading: false,
      //           selected_staging_sottoscrizione:
      //             res.data?.staging_sottoscrizione_by_pk,
      //         });
      //       },
      //       error: (error) => {
      //         patchState(store, {
      //           staging_sottoscrizione_loading: false,
      //           selected_staging_sottoscrizione: undefined,
      //           failure: error,
      //         });
      //         _toast.add({
      //           key: 'global-toast',
      //           severity: 'error',
      //           summary: _translate.instant(`toast.error.title`),
      //           detail: _translate.instant(`toast.error.detail`, {
      //             error,
      //           }),
      //         });
      //       },
      //     });
      //   },
      //   patchSelectedStagingSottocrizione: (
      //     sottoscrizione: GetStagingInstallatoreSottoscrizioneQuery['staging_sottoscrizione_by_pk'],
      //   ) => {
      //     patchState(store, {
      //       selected_staging_sottoscrizione: sottoscrizione,
      //     });
      //   },
      //   patchSelectedLotto: (
      //     lotto: DeepPartial<
      //       GetStagingInstallatoreLottiQuery['staging_lotto'][0]
      //     >,
      //   ) => {
      //     patchState(store, {
      //       selected_lotto: lotto,
      //     });
      //   },
      //   initStagingLotti() {
      //     return runInInjectionContext(injector, () => {
      //       const _apollo = inject(ApolloRoleService);
      //       const _getter = _apollo.getClient('installatore');
      //       const _toast = inject(MessageService);
      //       const _translate = inject(TranslateService);

      //       clearSub('stagingLotti');

      //       const sub = _getter
      //         .subscribe<GetStagingInstallatoreLottiSubscriptionSubscription>({
      //           query: GetStagingInstallatoreLottiSubscriptionDocument,
      //           variables: {},
      //         })
      //         .subscribe({
      //           next: (res) => {
      //             patchState(store, {
      //               staging_lotti_data: res.data?.staging_lotto,
      //               staging_lotti_loading: false,
      //             });
      //           },
      //           error: (error) => {
      //             patchState(store, {
      //               staging_lotti_data: undefined,
      //               staging_lotti_loading: false,
      //               failure: error,
      //             });
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'error',
      //               summary: _translate.instant(`toast.error.title`),
      //               detail: _translate.instant(`toast.error.detail`, {
      //                 error,
      //               }),
      //             });
      //           },
      //         });

      //       setSub('stagingLotti', sub);
      //       return sub;
      //     });
      //   },
      //   getStagingLotti() {
      //     const _getter = inject(GetStagingInstallatoreLottiGQL);
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);

      //     return runInInjectionContext(injector, () => {
      //       return _getter.fetch(
      //         {},
      //         {
      //           context: {
      //             headers: { 'x-hasura-role': 'installatore' },
      //           },
      //         },
      //       );
      //     }).subscribe({
      //       next: (res) => {
      //         patchState(store, {
      //           staging_lotti_data: res.data?.staging_lotto,
      //         });
      //       },
      //       error: (error) => {
      //         patchState(store, {
      //           staging_lotti_data: undefined,
      //           failure: error,
      //         });
      //         _toast.add({
      //           key: 'global-toast',
      //           severity: 'error',
      //           summary: _translate.instant(`toast.error.title`),
      //           detail: _translate.instant(`toast.error.detail`, {
      //             error,
      //           }),
      //         });
      //       },
      //     });
      //   },
      //   updateStatoStagingSottoscrizioni: (
      //     ids: string[],
      //     stato: Staging__Stato_Sottoscrizione_Enum,
      //   ) => {
      //     return runInInjectionContext(injector, () => {
      //       const _mutation = inject(UpdateStatoStagingSottoscrizioniGQL);
      //       const _toast = inject(MessageService);
      //       const _translate = inject(TranslateService);
      //       const _loading = inject(LoadingService);

      //       _loading.start();

      //       return _mutation
      //         .mutate(
      //           {
      //             ids,
      //             stato,
      //           },
      //           {
      //             context: {
      //               headers: { 'x-hasura-role': 'installatore' },
      //             },
      //           },
      //         )
      //         .pipe(
      //           tapResponse({
      //             next: (res) => {
      //               _loading.stop();
      //               return res;
      //             },
      //             error: (error) => {
      //               patchState(store, {
      //                 failure: error,
      //               });
      //               _loading.stop();
      //               _toast.add({
      //                 key: 'global-toast',
      //                 severity: 'error',
      //                 summary: _translate.instant('toast.error.title'),
      //                 detail: _translate.instant('toast.error.detail', {
      //                   error,
      //                 }),
      //               });
      //             },
      //           }),
      //         );
      //     });
      //   },
      //   deleteStagingLottoCompleto: (
      //     sottoscrizione_ids: string[],
      //     lotto_ids: string[],
      //   ) => {
      //     return runInInjectionContext(injector, () => {
      //       const _mutation = inject(DeleteStagingLottoCompletoGQL);
      //       const _toast = inject(MessageService);
      //       const _translate = inject(TranslateService);
      //       const _loading = inject(LoadingService);

      //       _loading.start();

      //       return _mutation
      //         .mutate(
      //           {
      //             sottoscrizione_ids,
      //             lotto_ids,
      //           },
      //           {
      //             context: {
      //               headers: { 'x-hasura-role': 'installatore' },
      //             },
      //           },
      //         )
      //         .pipe(
      //           tapResponse({
      //             next: (res) => {
      //               _loading.stop();
      //               _toast.add({
      //                 key: 'global-toast',
      //                 severity: 'success',
      //                 summary: _translate.instant(
      //                   'staging.sottoscrizioni.toast.success.title',
      //                 ),
      //                 detail: _translate.instant(
      //                   'staging.sottoscrizioni.toast.delete.success.detail',
      //                   {
      //                     count: res.data?.delete_lotto?.affected_rows || 0,
      //                   },
      //                 ),
      //               });
      //               return res;
      //             },
      //             error: (error) => {
      //               patchState(store, {
      //                 failure: error,
      //               });
      //               _loading.stop();
      //               _toast.add({
      //                 key: 'global-toast',
      //                 severity: 'error',
      //                 summary: _translate.instant(
      //                   'staging.sottoscrizioni.toast.error.title',
      //                 ),
      //                 detail: _translate.instant(
      //                   'staging.sottoscrizioni.toast.error.detail',
      //                   {
      //                     error,
      //                   },
      //                 ),
      //               });
      //             },
      //           }),
      //         );
      //     });
      //   },
    };
  }),
);
